import type { Sort } from "./useListParams";

/* Sorting rows that are already on screen, by every level of the sort.
 *
 * For the tables whose rows are shaped after they arrive, such as a
 * company's people, which are collapsed to one row per person and ranked by
 * reach before they are shown. The lists sort on the server (applySorts in
 * useListParams.ts), because they page. This is presentation only: it orders
 * what the API returned and computes nothing the lake could have answered.
 *
 * A missing value sorts last whichever way the column runs, so the rows a
 * person can act on come first in both directions. */
export type SortValue = string | number | null | undefined;

export function sortRows<Row>(
  rows: readonly Row[],
  sorts: readonly Sort[],
  get: (row: Row, column: string) => SortValue,
  tiebreaks: readonly Sort[] = [],
): Row[] {
  const levels = [...sorts, ...tiebreaks.filter((t) => !sorts.some((s) => s.column === t.column))];
  return [...rows].sort((a, b) => {
    for (const level of levels) {
      const av = get(a, level.column);
      const bv = get(b, level.column);
      const aNull = av === null || av === undefined || av === "";
      const bNull = bv === null || bv === undefined || bv === "";
      if (aNull && bNull) continue;
      if (aNull) return 1;
      if (bNull) return -1;
      const cmp = typeof av === "number" && typeof bv === "number"
        ? av - bv
        : String(av).localeCompare(String(bv), undefined, { sensitivity: "base", numeric: true });
      if (cmp !== 0) return level.ascending ? cmp : -cmp;
    }
    return 0;
  });
}
